import type { DocumentTypeRemovalImpact } from '../shared/desktop-api';

const counted = (count: number, singular: string, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`;

type RemovalCounts = Pick<DocumentTypeRemovalImpact, 'documentsToReview' | 'lastType'> & {
  documentsToRepick?: number;
  documentsWithConfirmedValues?: number;
};

/** Read again alongside others that wait for a page never read: "; 2 more wait for their unread pages". */
function stillUnreadClause(review: number, repick: number): string {
  const unread = review - repick;
  return unread > 0
    ? `; ${unread} more ${unread === 1 ? 'waits for its unread pages' : 'wait for their unread pages'}`
    : '';
}

/**
 * The documents read again that have values a person confirmed: one the type found has no field for keeps no type,
 * with its answers (D9), so it goes where an untyped one goes: it waits on Manual, it goes to the exceptions folder on
 * Autopilot (ADR-0096).
 */
function confirmedValuesClause(repick: number, confirmed: number, autopilot: boolean): string {
  if (confirmed <= 0) return '';
  const one = confirmed === 1;
  const also = autopilot
    ? one
      ? 'also goes there'
      : 'also go there'
    : one
      ? 'also waits'
      : 'also wait';
  if (repick === 1)
    return `; it ${autopilot ? 'also goes there' : 'also waits'} if the type found has no field for a value you confirmed`;
  return `; ${one ? 'the 1' : `any of the ${confirmed}`} with values you confirmed ${also} if the type found has no field for one of those values`;
}

/**
 * What Remove type says will happen to the type's unexported documents (D9; ADR-0096, decision 7 of
 * docs/planning/exceptions-destination.md). Each is read again against the remaining types with the normal type
 * question, never forced: a type that fits is used; with none, Manual holds it for a person to choose its type and
 * Autopilot sends it to the exceptions folder. The job's last type leaves them nothing to be read against: they wait
 * for a type on Manual and go to the exceptions folder on Autopilot. A document with a page never read is not counted
 * in `documentsToRepick`: it is read again once that page is read. One with a value a person confirmed
 * (`documentsWithConfirmedValues`) goes where an untyped one goes when the type found has no field for it. An engine
 * that does not count re-reads (no `documentsToRepick`) unfiles them for a person, as before.
 */
export function typeRemovalReviewLine(impact: RemovalCounts, autopilot: boolean): string {
  const review = impact.documentsToReview;
  const repick = impact.documentsToRepick ?? 0;
  if (review === 0) return 'No documents need a new type';
  if (repick > 0) {
    const none = autopilot
      ? repick === 1
        ? 'if none fits, it goes to the exceptions folder'
        : 'any that none fits go to the exceptions folder'
      : repick === 1
        ? 'if none fits, it waits for you to choose its type'
        : 'any that none fits wait for you to choose a type';
    return (
      `Paige will read ${counted(repick, 'document')} again against the other types; ${none}` +
      // The confirmed-values caveat qualifies those read again, so it follows their sentence, before the unread ones.
      confirmedValuesClause(
        repick,
        Math.min(impact.documentsWithConfirmedValues ?? 0, repick),
        autopilot,
      ) +
      stillUnreadClause(review, repick)
    );
  }
  if (impact.lastType)
    return autopilot
      ? `${counted(review, 'document')} will go to the exceptions folder`
      : `${counted(review, 'document')} will wait until you add a type`;
  if (impact.documentsToRepick !== undefined) {
    return `${counted(review, 'document')} ${review === 1 ? 'waits for its unread pages, then is read again against the other types' : 'wait for their unread pages, then are read again against the other types'}`;
  }
  return `${counted(review, 'document')} will go to review so you can choose ${review === 1 ? 'its' : 'their'} type`;
}

/**
 * The toast once the type is removed: the short form of the dialog's line. It gives the same counts but leaves out
 * the confirmed-values caveat.
 */
export function typeRemovalToast(
  typeName: string,
  removed: RemovalCounts,
  stillMoving: number,
  autopilot: boolean,
): string {
  const review = removed.documentsToReview;
  const repick = removed.documentsToRepick ?? 0;
  const unread = review - repick;
  const unreadPart =
    repick > 0 && unread > 0
      ? ` · ${unread} ${unread === 1 ? 'waits' : 'wait'} for unread pages`
      : '';
  if (repick > 0)
    return `“${typeName}” removed · ${counted(repick, 'document')} being read again` + unreadPart;
  if (review > 0 && !removed.lastType && removed.documentsToRepick !== undefined) {
    return `“${typeName}” removed · ${counted(review, 'document')} ${review === 1 ? 'waits' : 'wait'} for unread pages`;
  }
  // The job's last type is said before documents still moving: with no type left they never go to review on
  // Autopilot, they go to the exceptions folder.
  if (review > 0 && removed.lastType)
    return autopilot
      ? `“${typeName}” removed · ${counted(review, 'document')} ${review === 1 ? 'goes' : 'go'} to the exceptions folder`
      : `“${typeName}” removed · ${counted(review, 'document')} ${review === 1 ? 'waits' : 'wait'} for a type`;
  if (stillMoving > 0) return `“${typeName}” removed · its documents are moving to review`;
  return review > 0
    ? `“${typeName}” removed · ${counted(review, 'document')} to review`
    : `“${typeName}” removed`;
}
